/**
 * Watching the YouTube player that sits inside our own page.
 *
 * The server has always built the embed with enablejsapi=1 and recorded watch time when told —
 * the table, the endpoint and the completion rule were all there, and the comment beside the URL
 * even says this is how watch time is reported. Nothing ever reported it. A pupil could watch
 * thirty-six minutes to the last second and the lesson still said the video was unwatched,
 * because the page knew nothing about the video beyond its rectangle.
 *
 * This is the missing half.
 */

/** The slice of the IFrame API used here. The real surface is far larger and none of it is typed. */
interface YouTubePlayer {
  getCurrentTime(): number;
  getDuration(): number;
  destroy(): void;
}

interface YouTubeApi {
  Player: new (
    element: HTMLIFrameElement,
    options: {
      events: {
        onReady?: () => void;
        onStateChange?: (event: { data: number }) => void;
      };
    },
  ) => YouTubePlayer;
  PlayerState: { ENDED: number; PLAYING: number; PAUSED: number };
}

declare global {
  interface Window {
    YT?: YouTubeApi;
    onYouTubeIframeAPIReady?: () => void;
  }
}

import { countsAsWatching, REPORT_MS, SAMPLE_MS } from './watch-rule.ts';

export interface WatchReport {
  /** Seconds actually spent watching, which is not where the scrubber happens to be. */
  readonly watchedSeconds: number;
  /** The furthest point reached, so a player can resume where it left off. */
  readonly furthestSeconds: number;
  readonly durationSeconds: number;
  /** True the moment the player says it finished, so the page can react without waiting. */
  readonly ended: boolean;
}

/**
 * Loads the IFrame API once for the whole page.
 *
 * One shared promise, because the API is a single global that announces itself through a single
 * global callback: a second copy of the script would either be ignored or would overwrite the
 * callback the first one is still waiting on.
 */
let loading: Promise<YouTubeApi> | null = null;

function loadApi(): Promise<YouTubeApi> {
  if (window.YT?.Player) return Promise.resolve(window.YT);

  loading ??= new Promise<YouTubeApi>((resolve, reject) => {
    // The API script lives on youtube.com even when the embed itself is on youtube-nocookie.com.
    const script = document.createElement('script');
    script.src = 'https://www.youtube.com/iframe_api';
    script.async = true;

    const previous = window.onYouTubeIframeAPIReady;

    window.onYouTubeIframeAPIReady = () => {
      previous?.();

      if (window.YT?.Player) resolve(window.YT);
      else reject(new Error('YouTube API loaded without a player'));
    };

    script.onerror = () => reject(new Error('YouTube API could not be loaded'));
    document.head.appendChild(script);
  });

  return loading;
}

/**
 * Reports how much of a video was actually watched, until the returned function is called.
 *
 * Accumulated, not positional. Dragging the scrubber to the end puts the position at the end
 * while nothing was watched at all, and a lesson that accepts that teaches children to drag the
 * scrubber. Time therefore accrues only when the position advances by roughly one sampling
 * interval — playing forwards, at something like a normal speed.
 *
 * Every failure is silent and total: no API, no network, a blocked script — the video still
 * plays, and the lesson falls back to being completed by its other conditions. A lesson that
 * refuses to load because a tracker could not start would be a far worse trade.
 */
export function trackWatching(
  iframe: HTMLIFrameElement,
  report: (progress: WatchReport) => void,
): () => void {
  let stopped = false;
  let cleanUp: (() => void) | null = null;

  void loadApi()
    .then((api) => {
      if (stopped) return;

      let watched = 0;
      let furthest = 0;
      let previousPosition = 0;
      let sinceReport = 0;
      let finished = false;
      let timer: ReturnType<typeof setInterval> | null = null;

      const stopSampling = (): void => {
        if (timer === null) return;
        clearInterval(timer);
        timer = null;
      };

      const player: YouTubePlayer = new api.Player(iframe, {
        events: {
          onStateChange: (event) => {
            if (event.data === api.PlayerState.PLAYING) {
              // The position may have moved while paused; that gap was not watched.
              previousPosition = player.getCurrentTime();
              timer ??= setInterval(sample, SAMPLE_MS);
              return;
            }

            stopSampling();

            if (event.data === api.PlayerState.ENDED && !finished) {
              finished = true;

              /*
               * The last seconds are almost never sampled: people close the tab on the credits,
               * and whatever falls between the final tick and the end is missed. Reaching the end
               * is itself the fact worth recording, so the report is squared up to the duration.
               */
              const duration = player.getDuration();
              watched = Math.max(watched, duration);
              furthest = duration;
              const finishedReport = snapshot(true);
              if (worthSending(finishedReport)) report(finishedReport);
              return;
            }

            // Pause is worth reporting: it is the likeliest moment for the page to be closed.
            const paused = snapshot(false);
            if (worthSending(paused)) report(paused);
          },
        },
      });

      function snapshot(ended: boolean): WatchReport {
        const duration = Math.round(player.getDuration());

        return {
          // Never longer than the video: the server rightly refuses a watch time that exceeds it,
          // and rounding two independent numbers can put watched a second past the end.
          watchedSeconds: Math.min(Math.round(watched), duration),
          furthestSeconds: Math.min(Math.round(furthest), duration),
          durationSeconds: duration,
          ended,
        };
      }

      /*
       * Nothing is worth saying until the player knows how long the video is.
       *
       * It answers zero until the metadata arrives, and a report of zero is not a small
       * inaccuracy — it is meaningless, the server refuses it, and the refusal repeated every
       * fifteen seconds filled the logs with rejections that looked like a different bug
       * altogether. Silence is the honest answer while the length is unknown.
       */
      function worthSending(progress: WatchReport): boolean {
        return progress.durationSeconds > 0;
      }

      function sample(): void {
        const position = player.getCurrentTime();
        const moved = position - previousPosition;

        if (countsAsWatching(moved, SAMPLE_MS / 1000)) watched += moved;

        furthest = Math.max(furthest, position);
        previousPosition = position;
        sinceReport += SAMPLE_MS;

        if (sinceReport >= REPORT_MS) {
          sinceReport = 0;
          const periodic = snapshot(false);
          if (worthSending(periodic)) report(periodic);
        }
      }

      cleanUp = () => {
        stopSampling();
        // One last word before the page goes: everything since the previous report is otherwise
        // lost, and leaving a lesson is exactly when that matters most.
        const parting = snapshot(false);
        if (!finished && watched > 0 && worthSending(parting)) report(parting);
        player.destroy();
      };
    })
    .catch(() => {
      // Nothing to do and nothing to say: the video plays, it simply will not be counted.
    });

  return () => {
    stopped = true;
    cleanUp?.();
  };
}
